import type { Correction } from "@/queries/formality-request/types";
import type { FormDetailResponse } from "@/types/entities/formality-request";
import { cn } from "@/utils/theme";
import { Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { getDataTypeIcon, getDataTypeLabel } from "./utils";
import expedientRenderer from "../common/expedient";
import dateRangeRenderer from "./renderers/date-range";
import fileRenderer from "../common/file";
import numberRenderer from "../common/number";
import phoneRenderer from "../common/phone";
import ReadOnlyCorrection from "./corrections/read-only-correction";
import EditableCorrection from "./corrections/editable-correction";
import datetimeRangeRenderer from "../common/datetime-range";
import duiNitRenderer from "../common/dui-nit";
import filteredSelectRenderer from "../common/filtered-select";
import selectRenderer from "../common/select";
import switchRenderer from "../common/switch";

type FilledField = {
  id: string;
  value: unknown;
  updated_at: string;
  correction?: unknown;
  label?: string | null;
  unique_code?: string | null;
  name?: string | null;
  files?: Array<{ id: string; url: string; file_name: string }>;
};

type RenderProps = {
  obj: Fields;
  value: unknown;
  targetField: FilledField | undefined;
  fieldArr: FilledField[] | null;
};

const renderers: Record<string, (p: RenderProps) => React.ReactNode> = {
  switch: switchRenderer,
  expedient: expedientRenderer,
  datetime_range: datetimeRangeRenderer,
  dateRange: dateRangeRenderer,
  date_range: dateRangeRenderer,
  select: selectRenderer,
  file: fileRenderer,
  dui_nit: duiNitRenderer,
  filtered_select: filteredSelectRenderer,
  "filtered-list": filteredSelectRenderer,
  filtered_list: filteredSelectRenderer,
  number: numberRenderer,
  phone: phoneRenderer,
};

export type Fields =
  FormDetailResponse["steps"][number]["sections"][number]["fields"][number];

const columnHelper = createColumnHelper<Fields>();
export const tableColumns = ({
  corrections,
  onCorrection,
  onOpenCorrectionModal,
  onShowFieldHistory,
  currentGroupId,
  lockedApprovedFields,
  isReadOnly = false,
  existingCorrection,
  groupsCount,
  nonPreventableFields,
  fieldStartIndex = 0,
}: Readonly<{
  onOpenCorrectionModal: (fieldId: string) => void;
  onCorrection: (fieldId: string, status: "DENIED" | "APPROVED") => void;
  onShowFieldHistory: (field: Fields) => void;
  corrections: Map<string, { status: "DENIED" | "APPROVED"; reason?: string }>;
  currentGroupId?: string | null;
  lockedApprovedFields?: Set<string>;
  nonPreventableFields?: Set<string>;
  isReadOnly?: boolean;
  existingCorrection?: Correction;
  groupsCount: number;
  fieldStartIndex?: number;
}>) => {
  return [
    columnHelper.display({
      id: "field_number",
      header: "#",
      meta: {
        className:
          "align-top min-w-[48px] px-3 py-[32px] text-center align-center",
      },
      cell: ({ row }) => (
        <span className="text-body text-base tabular-nums">
          {fieldStartIndex + row.index + 1}
        </span>
      ),
    }),
    columnHelper.accessor("name", {
      header: "Tipo de dato",
      meta: {
        className:
          "align-top min-w-0 w-[45%] max-w-[50%] lg:w-[353px] lg:min-w-[353px] lg:max-w-[353px] !whitespace-normal break-words overflow-hidden",
      },
      cell: (info) => {
        if (!info.getValue()) {
          return <p>&nbsp;</p>;
        }
        const field = info.row.original;
        return (
          <div className="w-full flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="neutral" shape="pill" fill="ghost" size="sm">
                <Icon
                  type="fas"
                  name={getDataTypeIcon(field.type)}
                  size="sm"
                  className="text-body-quiet"
                />
                <span>{getDataTypeLabel(field.type)}</span>
              </Badge>
              <span
                className={
                  field.required
                    ? "text-info text-xs font-semibold"
                    : "text-body-quiet text-xs"
                }
              >
                {field.required ? "Requerido" : "Opcional"}
              </span>
            </div>
            <p className="font-semibold text-body-loud text-size-body wrap-break-words min-w-0">
              {field.label}
            </p>
            {!field?.is_preventable && (
              <p className="text-warning font-normal text-xs">
                (No Observable)
              </p>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("filled_fields", {
      header: "Valor",
      meta: {
        className:
          "w-full overflow-hidden !whitespace-normal break-all align-center",
      },
      cell: (info) => {
        const fieldArr = info.getValue();
        const obj = info.row.original;
        const field = info.row.original;

        if ((!fieldArr || fieldArr.length === 0) && obj.type !== "switch") {
          return <span className="text-on-disabled-quiet">Vacío</span>;
        }

        // Get most recent filled_field with non-empty value for display
        const sorted = fieldArr
          ? [...fieldArr].sort(
              (a, b) =>
                new Date(b.updated_at).getTime() -
                new Date(a.updated_at).getTime(),
            )
          : [];
        const best = sorted.find(
          (ff) =>
            ff.value !== null && ff.value !== undefined && ff.value !== "",
        );
        const targetField = best || sorted.at(-1);
        const value = targetField?.value;

        const defaultContent: React.ReactNode = (
          <p className="wrap-break-word whitespace-pre-wrap text-size-body">
            {(value ?? "").toString()}
          </p>
        );

        let content: React.ReactNode;
        if (field.type === "interoperability") {
          const count = info.row.original.config.input_field.length || 0;
          const lastValue = fieldArr?.slice(-count);
          content = (
            <div className="flex flex-col gap-2">
              {lastValue?.map((filledField, index) => (
                <div
                  key={`${filledField.id}-${index}`}
                  className="flex flex-col"
                >
                  <p className="text-xs text-info font-semibold">
                    {filledField.label}:
                  </p>
                  <p className="break-normal whitespace-pre-wrap text-size-body">
                    {filledField.value === null ||
                    filledField.value === undefined ||
                    filledField.value === "" ? (
                      <span className="text-on-disabled-quiet">Vacío</span>
                    ) : (
                      filledField.value.toString()
                    )}
                  </p>
                </div>
              ))}
            </div>
          );
        } else {
          const renderer = renderers[obj.type];
          if (renderer) {
            content = renderer({ obj, value, targetField, fieldArr });
          } else if (Array.isArray(value)) {
            content = (
              <p className="wrap-break-word whitespace-pre-wrap text-size-body">
                {((value as Array<string>) || []).join(", ")}
              </p>
            );
          } else if (value === null || value === undefined || value === "") {
            content = <span className="text-on-disabled-quiet">Vacío</span>;
          } else {
            content = defaultContent;
          }
        }

        return (
          <div className="flex w-full items-center gap-2">
            <div className="min-w-0 flex-1">{content}</div>
            <Button
              size="icon"
              variant="quiet"
              type="button"
              aria-label="correction-history"
              className="shrink-0"
              onClick={() => onShowFieldHistory(field)}
            >
              <Icon name="clock" type="far" />
            </Button>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      meta: {
        className: "w-[48px] border-transparent border-x-2 align-center",
      },
      cell: (info) => {
        const filledFields = info.row.original.filled_fields;
        const isPreventable = info.row.original.is_preventable;

        const lastWithCorrection = filledFields
          ? [...filledFields].reverse().find((ff) => ff.correction)
          : undefined;
        const targetField =
          lastWithCorrection || filledFields?.[filledFields.length - 1];
        const filledFieldId = targetField?.id;

        if (nonPreventableFields?.has(filledFieldId || "") || !isPreventable) {
          return;
        }

        // Read-only mode — show existing correction status
        if (isReadOnly && existingCorrection) {
          const fieldCorrection = filledFieldId
            ? existingCorrection.field_corrections?.find(
                (fc) => fc.filled_field_id === filledFieldId,
              )
            : undefined;
          if (!fieldCorrection) return null;
          return (
            <ReadOnlyCorrection
              fieldId={filledFieldId}
              correction={fieldCorrection}
              onOpenModal={onOpenCorrectionModal}
            />
          );
        }

        // Editable mode — show approve/deny buttons
        const fieldIdentifier =
          filledFieldId || `field_${info.row.original.id}`;
        const hasPreviousCorrectionApproved =
          lockedApprovedFields?.has(filledFieldId || "") || false;
        const isLockedApproved =
          currentGroupId && groupsCount > 1
            ? false
            : (lockedApprovedFields?.has(filledFieldId || "") ?? false);
        const correction = corrections.get(fieldIdentifier);

        return (
          <div
            className={cn(
              "flex gap-3",
              hasPreviousCorrectionApproved && "justify-start",
            )}
          >
            <EditableCorrection
              isLockedApproved={isLockedApproved}
              hasPreviousCorrectionApproved={hasPreviousCorrectionApproved}
              correction={correction}
              onApprove={() => onCorrection(fieldIdentifier, "APPROVED")}
              onDeny={() => onCorrection(fieldIdentifier, "DENIED")}
              onOpenModal={() => onOpenCorrectionModal(fieldIdentifier)}
            />
          </div>
        );
      },
    }),
  ] as ColumnDef<Fields>[];
};
